import { Checkbox, Collapse, TableCell } from '@mui/material';
import { Fragment, useCallback } from 'react';

import { CustomTooltip } from '../tooltip/CustomTooltip';
import { Column, CustomTableRowProps } from './CustomTable.interface';
import { StyledTableRow, TableCellNameText } from './CustomTable.styled';

export const CustomTableRow = <T extends Record<string, any>>({
  columns,
  row,
  renderActions,
  renderTags,
  renderCollapseContent,
  expanded = false,
  onToggle,
  onRowClick,
  tableRowProps,
  customCellRenderer,
}: CustomTableRowProps<T>) => {
  const handleToggle = useCallback(() => {
    if (renderCollapseContent && onToggle) {
      onToggle();
    }
  }, [renderCollapseContent, onToggle]);

  const handleRowClick = useCallback(() => {
    if (onRowClick) {
      onRowClick(row);
    } else {
      handleToggle();
    }
  }, [onRowClick, row, handleToggle]);

  return (
    <Fragment>
      <StyledTableRow
        onClick={handleRowClick}
        sx={{
          cursor: onRowClick || renderCollapseContent ? 'pointer' : 'default',
        }}
        selected={expanded}
        {...tableRowProps}>
        {columns.map((col: Column, colIdx: number) => {
          if (customCellRenderer?.[col.id]) {
            return <> {customCellRenderer[col.id](row, col)}</>;
          }
          const isNameColumn = colIdx === 0;

          return (
            <TableCell key={colIdx} align={col.align || 'left'}>
              {col.id === 'actions' ? (
                renderActions?.(row, col, expanded)
              ) : col.id === 'tags' ? (
                renderTags?.(row)
              ) : col.id === 'checkbox' && row.checkbox ? (
                <Checkbox
                  checked={row.checkbox.isSelected}
                  onChange={row.checkbox.onSelect}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <CustomTooltip
                  width={450}
                  placement="bottom"
                  cursor="inherit"
                  title={row[col.id]}
                  overflowLineNumber={1}>
                  <TableCellNameText $width={isNameColumn && 450}>
                    {row[col.id]}
                  </TableCellNameText>
                </CustomTooltip>
              )}
            </TableCell>
          );
        })}
      </StyledTableRow>

      {renderCollapseContent && (
        <StyledTableRow
          sx={{
            '& td': {
              borderBottom: 'none',
            },
          }}
          selected={expanded}>
          <TableCell style={{ padding: 0 }} colSpan={columns.length}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {renderCollapseContent(row)}
            </Collapse>
          </TableCell>
        </StyledTableRow>
      )}
    </Fragment>
  );
};
